import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, Clock, Users, ArrowLeft, CheckCircle, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { getTutorById, createBooking } from '../services/api';

export const TutorProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);

  useEffect(() => {
    fetchTutor();
  }, [id]);

  const fetchTutor = async () => {
    try {
      setLoading(true);
      const data = await getTutorById(id);
      setTutor(data);
    } catch (error) {
      console.error('Error fetching tutor:', error);
      toast({
        title: "Error",
        description: "Failed to load tutor profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async () => {
    if (!selectedSlot) {
      toast({
        title: "Please select a time slot",
        variant: "destructive",
      });
      return;
    }

    try {
      setBookingLoading(true);
      const bookingData = {
        tutor_id: id,
        student_name: "Demo Student",
        student_email: "student@example.com",
        student_phone: "+91 9876543210",
        booking_date: selectedDate.toISOString().split('T')[0],
        time_slot: selectedSlot
      };

      await createBooking(bookingData);

      toast({
        title: "Booking Confirmed!",
        description: `Your session with ${tutor.name} on ${selectedDate.toLocaleDateString()} at ${selectedSlot} has been booked.`,
      });
      setSelectedSlot(null);
    } catch (error) {
      console.error('Error creating booking:', error);
      toast({
        title: "Booking Failed",
        description: "Unable to create booking. Please try again.",
        variant: "destructive",
      });
    } finally {
      setBookingLoading(false);
    }
  };

  const timeSlots = [
    "09:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
    "02:00 PM", "03:00 PM", "04:00 PM", "05:00 PM"
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xl text-gray-600">Loading tutor profile...</p>
        </div>
      </div>
    );
  }

  if (!tutor) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xl text-gray-600">Tutor not found</p>
          <Button onClick={() => navigate('/find-tutor')} className="mt-4">
            Back to Tutors
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/find-tutor')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Tutors
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Tutor Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Card */}
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  <img
                    src={tutor.image}
                    alt={tutor.name}
                    className="w-full md:w-48 h-48 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">{tutor.name}</h1>
                        <p className="text-xl text-blue-600 font-medium">{tutor.subject}</p>
                      </div>
                      <div className="flex items-center space-x-1 bg-yellow-50 px-3 py-2 rounded-lg">
                        <Star className="h-5 w-5 text-yellow-500 fill-current" />
                        <span className="text-lg font-bold text-gray-900">{tutor.rating}</span>
                      </div>
                    </div>

                    <p className="text-gray-700 mb-4">{tutor.bio}</p>

                    <div className="flex flex-wrap gap-4 mb-4">
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Clock className="h-5 w-5" />
                        <span className="font-medium">{tutor.experience} experience</span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Users className="h-5 w-5" />
                        <span className="font-medium">{tutor.students} students taught</span>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <p className="text-3xl font-bold text-gray-900">₹{tutor.hourly_rate} <span className="text-base font-normal text-gray-600">per hour</span></p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Subjects Taught */}
            <Card>
              <CardHeader>
                <CardTitle>Subjects & Expertise</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {(tutor.subjects || []).map((subject, index) => (
                    <Badge key={index} variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-100">
                      {subject}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Availability */}
            <Card>
              <CardHeader>
                <CardTitle>Regular Availability</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(tutor.availability || []).map((slot, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="text-gray-700">{slot}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Booking */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <CalendarIcon className="h-5 w-5" />
                  <span>Book a Session</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Calendar */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">Select Date</p>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    className="rounded-md border"
                    disabled={(date) => date < new Date()}
                  />
                </div>

                {/* Time Slots */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">Select Time Slot</p>
                  <div className="grid grid-cols-2 gap-2">
                    {timeSlots.map((slot) => (
                      <button
                        key={slot}
                        onClick={() => setSelectedSlot(slot)}
                        className={`py-2 px-3 text-sm rounded-lg border transition-all ${selectedSlot === slot
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
                          }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Booking Summary */}
                {selectedSlot && (
                  <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                    <p className="text-sm font-medium text-gray-700">Booking Summary</p>
                    <p className="text-sm text-gray-600">Date: {selectedDate.toLocaleDateString()}</p>
                    <p className="text-sm text-gray-600">Time: {selectedSlot}</p>
                    <p className="text-sm text-gray-600">Duration: 1 hour</p>
                    <p className="text-lg font-bold text-gray-900 pt-2 border-t border-blue-200">
                      Total: ₹{tutor.hourly_rate}
                    </p>
                  </div>
                )}

                {/* Book Button */}
                <Button
                  onClick={handleBooking}
                  disabled={bookingLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  size="lg"
                >
                  {bookingLoading ? 'Booking...' : 'Confirm Booking'}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  You'll receive a confirmation email after booking
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
